import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getMyCourseDetail, courseErrorMessage } from "../courses";
import PracticeSentenceModal from "../components/Dashboard/PracticeSentenceModal";

const scoreBadgeClass = (score) => {
    if (score === null || score === undefined) return "bg-gray-100 text-gray-500";
    if (score >= 85) return "bg-green-100 text-green-800";
    if (score >= 65) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
};

// Student view of one joined course: folders ("homework weeks") with their
// sentences, own best score per sentence, and a click-to-practice flow.
const CoursePage = () => {
    const { courseId } = useParams();
    const { t } = useTranslation();
    const [course, setCourse] = useState(null);
    const [error, setError] = useState(null);
    const [practiceSentence, setPracticeSentence] = useState(null);

    const loadCourse = useCallback(() => {
        getMyCourseDetail(courseId)
            .then(setCourse)
            .catch((err) => setError(courseErrorMessage(err, t)));
    }, [courseId]);

    useEffect(() => {
        loadCourse();
    }, [loadCourse]);

    if (error) {
        return (
            <div className="px-4 pt-8 md:px-20 text-center">
                <p className="text-red-600">{error}</p>
                <Link to="/dashboard" className="mt-4 inline-block text-primary underline">
                    {t("dashboard")}
                </Link>
            </div>
        );
    }

    if (!course) {
        return (
            <div className="px-4 pt-8 md:px-20">
                <div className="h-8 w-1/2 animate-pulse rounded bg-gray-200" />
                <div className="mt-6 h-32 animate-pulse rounded bg-gray-200" />
            </div>
        );
    }

    const { practiced_sentences, total_sentences, average_score } = course.progress;
    const percent = total_sentences
        ? Math.round((practiced_sentences / total_sentences) * 100)
        : 0;

    const sections = [
        ...course.folders.map((folder) => ({
            key: `folder-${folder.id}`,
            name: folder.name,
            sentences: folder.sentences,
        })),
        ...(course.unfiled_sentences.length
            ? [{
                  key: "unfiled",
                  name: t("courses.unfiled"),
                  sentences: course.unfiled_sentences,
              }]
            : []),
    ];

    // All sentence texts in display order; "next sentence" in the practice
    // modal walks this list and wraps around at the end.
    const orderedTexts = sections.flatMap((section) =>
        section.sentences.map((sentence) => sentence.text)
    );

    const handleNextSentence = () => {
        const index = orderedTexts.indexOf(practiceSentence);
        setPracticeSentence(orderedTexts[(index + 1) % orderedTexts.length]);
        return Promise.resolve();
    };

    const renderSentence = (sentence) => (
        <li key={sentence.id}>
            <button
                className="w-full text-left rounded-md border border-border bg-white p-3 hover:bg-gray-50 transition-colors flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                onClick={() => setPracticeSentence(sentence.text)}
            >
                <span className="min-w-0">{sentence.text}</span>
                <span className="flex items-center gap-2 shrink-0 text-xs">
                    {sentence.attempts > 0 && (
                        <span className="text-gray-500">
                            {sentence.attempts}× {t("courses.practiced")}
                        </span>
                    )}
                    <span
                        className={`rounded px-2 py-0.5 font-medium ${scoreBadgeClass(sentence.best_score)}`}
                    >
                        {sentence.best_score !== null
                            ? `${sentence.best_score} ${t("unit.points")}`
                            : t("courses.notPracticed")}
                    </span>
                </span>
            </button>
        </li>
    );

    return (
        <div className="px-4 pt-4 md:px-20 md:pt-10 flex flex-col gap-4 pb-10">
            <div>
                <Link to="/dashboard" className="text-sm text-gray-500 hover:underline">
                    ← {t("dashboard")}
                </Link>
                <h1 className="mt-1 text-2xl md:text-3xl font-semibold">{course.title}</h1>
                <p className="text-sm text-gray-600">
                    {t("courses.taughtBy")} {course.teacher_email}
                </p>
            </div>

            {/* Progress summary */}
            <div className="rounded-lg border border-border bg-secondary p-4 md:p-6">
                <div className="flex items-center justify-between flex-wrap gap-2 text-sm text-gray-700">
                    <span>
                        {practiced_sentences}/{total_sentences} {t("courses.sentencesPracticed")}
                    </span>
                    {average_score !== null && (
                        <span>
                            {t("courses.averageScore")}: {average_score} {t("unit.points")}
                        </span>
                    )}
                </div>
                <div className="mt-2 h-2.5 w-full rounded-full bg-gray-200">
                    <div
                        className="h-2.5 rounded-full bg-primary"
                        style={{ width: `${percent}%` }}
                    />
                </div>
            </div>

            {sections.length === 0 && (
                <p className="text-gray-600">{t("courses.noSentencesYet")}</p>
            )}

            {sections.map((section) => (
                <div key={section.key} className="rounded-lg border border-border bg-secondary p-4 md:p-6">
                    <h2 className="font-medium">{section.name}</h2>
                    {section.sentences.length === 0 ? (
                        <p className="mt-2 text-sm text-gray-500">{t("courses.emptyFolder")}</p>
                    ) : (
                        <ul className="mt-3 flex flex-col gap-2">
                            {section.sentences.map(renderSentence)}
                        </ul>
                    )}
                </div>
            ))}

            {practiceSentence && (
                <PracticeSentenceModal
                    key={practiceSentence}
                    sentence={practiceSentence}
                    selectedLanguage={course.language}
                    onNextSentence={handleNextSentence}
                    allPracticed={total_sentences > 0 && practiced_sentences === total_sentences}
                    // Always refresh on close: advancing inside the modal
                    // remounts it, which would lose the modal's own
                    // "practiced" flag for earlier sentences.
                    onClose={() => {
                        setPracticeSentence(null);
                        loadCourse();
                    }}
                />
            )}
        </div>
    );
};

export default CoursePage;
